import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { IThemeService } from '../../interfaces/theme-service.interface';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { iThemeSetting } from './app-theme-setting.enum';
import { Colour, Palette, Theme } from './app-theme.interface';
import { CLMI_DARK_THEME_VARS } from './clmi-dark-theme-vars.constants';
import { DARK_THEME_VARS } from './dark-theme-vars.constants';

@Injectable({ providedIn: 'root' })
export class WdxAppThemeService implements IThemeService {
    private VARIABLE_PREFIX = 'bs-';
    private LOCAL_STORAGE_KEY = 'ThemeSetting';
    private _theme!: Theme;
    private darkThemeCSSVariables: string;
    private osThemeMediaQuery?: MediaQueryList;
    private osThemeChangeHandler?: (event: MediaQueryListEvent) => void;

    themeCSSVariables!: string;
    themeSetting!: iThemeSetting;
    currentTheme$ = new BehaviorSubject<iThemeSetting>(iThemeSetting.Light);

    private localStorageService = inject(LocalStorageService);

    constructor() {
        this.getThemeSettingFromLocalStorage();

        this.darkThemeCSSVariables = [
            this.setKeyValueObjectAsCSSVariables(DARK_THEME_VARS),
            this.setKeyValueObjectAsCSSVariables(CLMI_DARK_THEME_VARS, 'clmi-'),
        ].join(' ');
    }

    set(theme: Theme, themeSetting?: iThemeSetting) {
        this._theme = theme;
        if (themeSetting) {
            this.themeSetting = themeSetting;
        }
        this.setThemeAsCSSVariables(theme);
        this.updateThemeSetting();
    }

    get theme() {
        return this._theme;
    }

    getThemeValue(themeVariable: string): string {
        return window
            .getComputedStyle(document.documentElement, null)
            .getPropertyValue(`--bs-${themeVariable}`);
    }

    setToLight(): void {
        this.cleanupOSThemeListener();
        this.themeSetting = iThemeSetting.Light;
        this.updateCurrentTheme(iThemeSetting.Light);
        this.applyThemeToDocument(false);
        this.updateLocalStorage(iThemeSetting.Light);
    }

    setToDark(): void {
        this.cleanupOSThemeListener();
        this.updateCurrentTheme(iThemeSetting.Dark);
        this.themeSetting = iThemeSetting.Dark;
        this.applyThemeToDocument(true);
        this.updateLocalStorage(iThemeSetting.Dark);
    }

    setToOS(): void {
        this.themeSetting = iThemeSetting.OS;
        this.updateLocalStorage(iThemeSetting.OS);

        const prefersDark = this.getOSThemePreference();

        this.applyThemeToDocument(prefersDark);
        this.updateCurrentTheme(
            prefersDark ? iThemeSetting.Dark : iThemeSetting.Light,
        );
        this.setupOSThemeListener();
    }

    private getOSThemePreference(): boolean {
        if (typeof window === 'undefined' || !window.matchMedia) {
            // Fallback to light theme if matchMedia is not available (SSR/older browsers)
            return false;
        }
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }

    private setupOSThemeListener(): void {
        if (typeof window === 'undefined' || !window.matchMedia) {
            return;
        }

        this.cleanupOSThemeListener();
        this.osThemeMediaQuery = window.matchMedia(
            '(prefers-color-scheme: dark)',
        );

        this.osThemeChangeHandler = (event: MediaQueryListEvent) => {
            const prefersDark = event.matches;
            this.applyThemeToDocument(prefersDark);
            this.updateCurrentTheme(
                prefersDark ? iThemeSetting.Dark : iThemeSetting.Light,
            );
        };

        if (this.osThemeMediaQuery.addEventListener) {
            this.osThemeMediaQuery.addEventListener(
                'change',
                this.osThemeChangeHandler,
            );
        } else {
            // Fallback for older browsers
            this.osThemeMediaQuery.addListener(
                this.osThemeChangeHandler as any,
            );
        }
    }

    private cleanupOSThemeListener(): void {
        if (this.osThemeMediaQuery && this.osThemeChangeHandler) {
            if (this.osThemeMediaQuery.removeEventListener) {
                this.osThemeMediaQuery.removeEventListener(
                    'change',
                    this.osThemeChangeHandler,
                );
            } else {
                // Fallback for older browsers
                this.osThemeMediaQuery.removeListener(
                    this.osThemeChangeHandler as any,
                );
            }
        }
        this.osThemeMediaQuery = undefined;
        this.osThemeChangeHandler = undefined;
    }

    updateThemeSetting(): void {
        switch (this.themeSetting) {
            case iThemeSetting.Light:
                this.setToLight();
                break;
            case iThemeSetting.Dark:
                this.setToDark();
                break;
            case iThemeSetting.OS:
                this.setToOS();
                break;
        }
    }

    private setThemeAsCSSVariables(theme: Theme): void {
        const cssVariables = [
            this.setPaletteAsCSSVariables(theme.base as Palette),
            this.setPaletteAsCSSVariables(theme.light as Palette, 'light'),
            this.setPaletteAsCSSVariables(theme.dark as Palette, 'dark'),
        ];
        this.themeCSSVariables = cssVariables.join(' ');

        this.applyThemeToDocument(false);
    }

    private setPaletteAsCSSVariables(
        palette: Palette,
        suffix?: string,
    ): string {
        if (!palette) {
            return '';
        }
        return Object.keys(palette)
            .map((color) =>
                this.setColorRgb(
                    color,
                    suffix as string,
                    (palette as any)[color] as Colour,
                ),
            )
            .join(' ');
    }

    private setColorRgb(color: string, suffix: string, rgb: Colour): string {
        if (!rgb) {
            return '';
        }
        const variable = `--${this.VARIABLE_PREFIX}${color}${
            suffix ? '-' + suffix : ''
        }`;
        return [
            `${variable}-red: ${rgb.r};`,
            `${variable}-green: ${rgb.g};`,
            `${variable}-blue: ${rgb.b};`,
        ].join(' ');
    }

    private getThemeSettingFromLocalStorage(): void {
        this.themeSetting =
            (this.localStorageService.getStringKey(
                this.LOCAL_STORAGE_KEY,
            ) as iThemeSetting) || iThemeSetting.Light;
    }

    private updateLocalStorage(themeSetting: iThemeSetting): void {
        this.localStorageService.setStringKey(
            this.LOCAL_STORAGE_KEY,
            themeSetting,
        );
    }

    private applyThemeToDocument(darkTheme: boolean): void {
        const theme = [
            this.themeCSSVariables,
            ...(darkTheme ? [this.darkThemeCSSVariables] : []),
        ].join(' ');
        document.documentElement.setAttribute('style', theme);
        this.updateCurrentTheme(
            darkTheme ? iThemeSetting.Dark : iThemeSetting.Light,
        );
    }

    private setKeyValueObjectAsCSSVariables(
        obj: any,
        keyPrefix?: string,
        valuePrefix?: string,
    ): string {
        if (!obj) {
            return '';
        }
        return Object.keys(obj)
            .map(
                (color) =>
                    `--${keyPrefix || this.VARIABLE_PREFIX}${color}: var(--${
                        valuePrefix || this.VARIABLE_PREFIX
                    }${obj[color]});`,
            )
            .join(' ');
    }

    private updateCurrentTheme(theme: iThemeSetting): void {
        this.currentTheme$.next(theme);
    }
}
